import { Banner, Card, Select, Skeleton } from '@servicecompressionllc/sc-design-system';
import { AppShell } from '../../components/AppShell.js';
import { Toggle } from '../../components/Toggle.js';
import { useServicePackagesLogic } from './useServicePackagesLogic.js';

export const ServicePackagesPage = () => {
  const { catalog, tenants, assignments, tenantId, mutation, selectTenant, setEnabled } =
    useServicePackagesLogic();
  const loadError = catalog.error ?? tenants.error ?? assignments.error;
  const error = loadError ?? mutation.error;
  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ink">Service packages</h1>
            <p className="mt-1 text-sm text-ink-muted">
              Choose which packages this tenant can use.
            </p>
          </div>
        </div>
        {error && (
          <Banner variant="danger">
            <Banner.Body>
              <Banner.Title>
                {loadError
                  ? 'Could not load service packages'
                  : 'Could not update service packages'}
              </Banner.Title>
              <Banner.Description>{error.message}</Banner.Description>
            </Banner.Body>
          </Banner>
        )}
        <div className="max-w-sm">
          <div className="mb-1 text-sm font-medium text-ink">Tenant</div>
          <Select
            value={tenantId ? [tenantId] : []}
            onChange={(v) => selectTenant(v[0] ?? '')}
            options={(tenants.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
            placeholder="Select a tenant"
          />
        </div>
        {catalog.isLoading || tenants.isLoading || assignments.isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <Card padding="none" className="divide-y divide-outline">
            {(catalog.data?.packages ?? []).map((pkg) => {
              const grant = assignments.data?.find((a) => a.packageId === pkg.id);
              return (
                <div key={pkg.id} className="flex flex-wrap items-start justify-between gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <h2 className="font-medium text-ink">{pkg.displayName}</h2>
                    {pkg.description && (
                      <p className="mt-1 text-sm text-ink-muted">{pkg.description}</p>
                    )}
                    <p className="mt-2 text-sm text-ink-muted">
                      Includes {pkg.capabilities.map((m) => m.capability.displayName).join(', ')}
                    </p>
                    {grant?.enabled && (
                      <p className="mt-1 text-xs text-ink-muted">
                        Granted by {grant.grantedByEmail} on{' '}
                        {new Date(grant.grantedAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <Toggle
                      label={`${pkg.displayName} for this tenant`}
                      checked={grant?.enabled ?? false}
                      disabled={!tenantId || assignments.isError || mutation.isPending}
                      onChange={() => setEnabled(pkg.id, !grant?.enabled)}
                    />
                  </div>
                </div>
              );
            })}
            {catalog.data?.packages.length === 0 && (
              <p className="p-4 text-sm text-ink-muted">No service packages yet.</p>
            )}
          </Card>
        )}
        <p className="text-sm text-ink-muted">
          Disabling a package keeps its configuration. A capability stays available if another
          enabled package includes it. User permissions still apply.
        </p>
      </div>
    </AppShell>
  );
};
